import {
    AnvilSelect,
    AnvilSelectOptionsProps,
    BodyText,
    Button,
    Icon,
    Stack,
} from '@servicetitan/design-system';
import { FC, useCallback, useMemo } from 'react';
import * as Styles from './pagination.module.less';

export interface PaginationProps {
    totalPages: number;
    currentPage: number;
    onJumpToPage: (page: number) => void;
}

const trigger = { clearable: false, size: 'small', className: Styles.paginationPage };
const valueIfTrue = (condition: boolean, value: string) => (condition ? value : undefined);

export const Pagination: FC<PaginationProps> = props => {
    const { currentPage, onJumpToPage, totalPages } = props;

    const options = useMemo(() => {
        return Array.from(
            { length: totalPages },
            (_, i) =>
                ({
                    text: `${i + 1}`,
                    value: i + 1,
                    selected: i + 1 === currentPage,
                }) as AnvilSelectOptionsProps
        );
    }, [currentPage, totalPages]);

    const handleCurrentPageChange = useCallback(
        (anvilProps: AnvilSelectOptionsProps | AnvilSelectOptionsProps[]) => {
            onJumpToPage((anvilProps as AnvilSelectOptionsProps).value);
        },
        [onJumpToPage]
    );

    const prevDisabled = currentPage === 1;
    const nextDisabled = currentPage === totalPages;

    return (
        <Stack alignItems="center" direction="row" spacing={1}>
            <Button
                fill="subtle"
                small
                disabled={prevDisabled}
                icon={<Icon color={valueIfTrue(!prevDisabled, '#0265DC')} name="skip_previous" />}
                onClick={() => onJumpToPage(1)}
                aria-label="First Page"
            />
            <Button
                fill="subtle"
                small
                disabled={prevDisabled}
                icon={
                    <Icon
                        color={valueIfTrue(!prevDisabled, '#0265DC')}
                        className={Styles.paginationPreviousPageIcon}
                        name="play_arrow"
                    />
                }
                onClick={() => onJumpToPage(currentPage - 1)}
                aria-label="Previous Page"
            />
            <Stack direction="row" spacing={1} alignItems="center">
                <AnvilSelect
                    trigger={trigger}
                    options={options}
                    onChange={handleCurrentPageChange}
                    value={currentPage}
                    scrollHeight="350px"
                    emptyResult=""
                />
                <BodyText className="flex-shrink-0">of {totalPages}</BodyText>
            </Stack>
            <Button
                fill="subtle"
                small
                disabled={nextDisabled}
                icon={<Icon color={valueIfTrue(!nextDisabled, '#0265DC')} name="play_arrow" />}
                onClick={() => onJumpToPage(currentPage + 1)}
                aria-label="Next Page"
            />
            <Button
                fill="subtle"
                small
                disabled={nextDisabled}
                icon={<Icon color={valueIfTrue(!nextDisabled, '#0265DC')} name="skip_next" />}
                onClick={() => onJumpToPage(totalPages)}
                aria-label="Last Page"
            />
        </Stack>
    );
};
